import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Cache the API key to avoid repeated fetches
let cachedApiKey: string | null = null;
let fetchPromise: Promise<string> | null = null;

export function useGoogleMapsApiKey(enabled = true) {
  const [apiKey, setApiKey] = useState<string>(cachedApiKey || "");
  const [isLoading, setIsLoading] = useState(!cachedApiKey);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) return;
    // If already cached, use it immediately
    if (cachedApiKey) {
      setApiKey(cachedApiKey);
      setIsLoading(false);
      return;
    }

    // If a fetch is already in progress, wait for it
    if (fetchPromise) {
      fetchPromise
        .then((key) => {
          setApiKey(key);
          setIsLoading(false);
        })
        .catch((err) => {
          setError(err);
          setIsLoading(false);
        });
      return;
    }

    // Start a new fetch
    fetchPromise = (async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke(
          "get-google-maps-key"
        );

        if (fnError) {
          throw new Error(fnError.message || "Failed to fetch Google Maps API key");
        }

        const key = data?.apiKey || "";
        cachedApiKey = key;
        return key;
      } catch (err) {
        cachedApiKey = null;
        fetchPromise = null;
        throw err;
      }
    })();

    fetchPromise
      .then((key) => {
        setApiKey(key);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsLoading(false);
      });
  }, [enabled]);

  return { apiKey, isLoading, error };
}

// Utility function for getting the API key (for use outside of React components)
export async function getGoogleMapsApiKey(): Promise<string> {
  if (cachedApiKey) {
    return cachedApiKey;
  }

  const { data, error } = await supabase.functions.invoke("get-google-maps-key");

  if (error) {
    throw new Error(error.message || "Failed to fetch Google Maps API key");
  }

  cachedApiKey = data?.apiKey || "";
  return cachedApiKey;
}
